import _extends from "@babel/runtime/helpers/extends";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Avatar, Badge, Button, Card, Grid, Icon, InputCheckbox, Link, Picture, Spinner, Text } from "~/components-shared";
import moment from "moment";
import styled, { useTheme } from "styled-components";
import lists from "~/lib/lists";
import { AuthoritiesEnum, TopicFootprintSourceEnum } from "~/types/enums";
import Markdown from "~/components/Markdown";
import { handleError, resolveSlackChannelName } from "~/lib/helpers";
import api from "~/lib/api";
import TopicFootprintActions from "~/components/TopicFootprintActions";
import useAbacExt from "~/hooks/abac";
import TopicFootprintNotes from "~/components/TopicFootprintNotes";
import TopicFootprintContentGmail from "~/components/TopicFootprintContentGmail";
import TopicFootprintContentNotion from "~/components/TopicFootprintContentNotion";
import TopicFootprintContentJira from "~/components/TopicFootprintContentJira";
import TopicFootprintContentSlack from "~/components/TopicFootprintContentSlack";
import TopicFootprintContentGoogleCalendar from "~/components/TopicFootprintContentGoogleCalendar";
import TopicFootprintContentDrive from "~/components/TopicFootprintContentDrive";
import TopicFootprintContentLinear from "~/components/TopicFootprintContentLinear";
import TopicFootprintContentAsana from "~/components/TopicFootprintContentAsana";
import FootprintActions from "~/components/FootprintActions";
const CloseButton = styled(Button)`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
`;
const StyledBadge = styled(Badge)`
  vertical-align: top;
`;
const AvatarGroup = styled.div`
  display: flex;
  flex-direction: column;

  & > *:not(:first-child) {
    border: 2px solid ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.background.primary;
}};
    margin-top: -2rem;
  }
`;
const Timeline = styled.div`
  position: relative;
  width: 6px;
  height: 100%;
  cursor: pointer;

  &:before {
    content: "";
    position: absolute;
    width: 2.4rem;
    height: 2.4rem;
    background-color: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.colors.background.primary;
}};
    border-radius: 50%;
    left: -1rem;
    top: 0.35rem;
    z-index: 1;
  }

  &:after {
    content: "";
    position: absolute;
    width: 2px;
    height: 1000px;
    background-color: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.colors.borders.primary;
}};
    right: 2px;
    top: 2rem;
    margin-top: -2px;
  }
`;
const SourcePicture = styled(Picture)`
  position: absolute;
  top: 0.8rem;
  left: calc(3px - 0.75rem);
  z-index: 1;
`;
export const groupFootprintContent = function (footprint) {
  let showDate = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  const content = () => {
    switch (footprint.source) {
      case TopicFootprintSourceEnum.SLACK:
        return /*#__PURE__*/React.createElement(TopicFootprintContentSlack, {
          footprint: footprint,
          hideDetails: true
        });
      case TopicFootprintSourceEnum.GOOGLE_CALENDAR:
        return /*#__PURE__*/React.createElement(TopicFootprintContentGoogleCalendar, {
          footprint: footprint,
          hideDetails: true
        });
      case TopicFootprintSourceEnum.NOTION:
        return /*#__PURE__*/React.createElement(TopicFootprintContentNotion, {
          footprint: footprint,
          hideDetails: true
        });
      case TopicFootprintSourceEnum.GOOGLE_SHEETS:
      case TopicFootprintSourceEnum.GOOGLE_DOCS:
      case TopicFootprintSourceEnum.GOOGLE_SLIDES:
        return /*#__PURE__*/React.createElement(TopicFootprintContentDrive, {
          footprint: footprint,
          hideDetails: true
        });
      case TopicFootprintSourceEnum.JIRA:
        return /*#__PURE__*/React.createElement(TopicFootprintContentJira, {
          footprint: footprint,
          hideDetails: true
        });
      case TopicFootprintSourceEnum.LINEAR:
        return /*#__PURE__*/React.createElement(TopicFootprintContentLinear, {
          footprint: footprint,
          hideDetails: true
        });
      case TopicFootprintSourceEnum.ASANA:
        return /*#__PURE__*/React.createElement(TopicFootprintContentAsana, {
          footprint: footprint,
          hideDetails: true
        });
      case TopicFootprintSourceEnum.GOOGLE_MAIL:
        return /*#__PURE__*/React.createElement(TopicFootprintContentGmail, {
          footprint: footprint,
          hideDetails: true
        });
      default:
        return null;
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold",
    display: "block"
  }, footprint.createdBy.firstName, " ", footprint.createdBy.lastName, " "), /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    weight: "semiBold",
    display: "block",
    s: {
      mb: 1
    }
  }, /*#__PURE__*/React.createElement(FootprintActions, {
    actions: footprint.actions
  }), " ", lists.topics.footprintTypes.nameArticles[footprint.type], " ", lists.topics.footprintTypes.names[footprint.type], showDate && ` on ${moment(footprint.date).format("ddd, MMM D hh:mma")}`, footprint.isRetro && ` • retro`), content());
};
export const groupContent = group => {
  const footprint = group.footprints[0];
  switch (group.source) {
    case TopicFootprintSourceEnum.SLACK:
      return /*#__PURE__*/React.createElement(Markdown, {
        weight: "semiBold",
        display: "block",
        color: "textSecondary",
        s: {
          mb: 1
        },
        text: `Had a conversation in ${resolveSlackChannelName(footprint.data.channelName, footprint.data.channelUrl)} Slack channel`
      });
    case TopicFootprintSourceEnum.GOOGLE_CALENDAR:
      return /*#__PURE__*/React.createElement(Markdown, {
        weight: "semiBold",
        display: "block",
        color: "textSecondary",
        s: {
          mb: 2
        },
        text: `Changed [Google Calendar](${footprint.data.url}) meeting`
      });
    case TopicFootprintSourceEnum.NOTION:
      return /*#__PURE__*/React.createElement(Markdown, {
        weight: "semiBold",
        display: "block",
        color: "textSecondary",
        s: {
          mb: 1
        },
        text: `Commented on a Notion document: [${footprint.data.emoji ? `${footprint.data.emoji} ` : ""}${footprint.data.title}](${footprint.data.url})`
      });
    case TopicFootprintSourceEnum.GOOGLE_SHEETS:
    case TopicFootprintSourceEnum.GOOGLE_DOCS:
    case TopicFootprintSourceEnum.GOOGLE_SLIDES:
      return /*#__PURE__*/React.createElement(Markdown, {
        weight: "semiBold",
        display: "block",
        color: "textSecondary",
        s: {
          mb: 2
        },
        text: `Commented on a ${lists.topics.footprintSources.names[footprint.source]} document: [${footprint.data.title}](${footprint.data.url})`
      });
    case TopicFootprintSourceEnum.JIRA:
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Markdown, {
        weight: "semiBold",
        display: "block",
        color: "textSecondary",
        s: {
          mb: 1
        },
        text: `Changed [${footprint.data.issueKey}](${footprint.data.issueUrl}) ${footprint.data.issueTypeName} in [${footprint.data.projectName}](${footprint.data.projectUrl})`
      }), /*#__PURE__*/React.createElement(Text, {
        weight: "semiBold",
        fontSize: 4,
        display: "block",
        s: {
          my: 1
        }
      }, footprint.data.issueTitle, /*#__PURE__*/React.createElement(Badge, {
        color: "grey",
        display: "inline-block",
        s: {
          ml: 2
        }
      }, footprint.data.issueStatusName)));
    case TopicFootprintSourceEnum.LINEAR:
      {
        const {
          issueKey,
          issueUrl,
          teamName,
          issueTitle,
          teamUrl,
          issueStatusName
        } = footprint.data;
        return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Markdown, {
          weight: "semiBold",
          display: "block",
          color: "textSecondary",
          s: {
            mb: 1
          },
          text: `Changed [${issueKey}](${issueUrl}) in [${teamName}](${teamUrl})`
        }), /*#__PURE__*/React.createElement(Text, {
          weight: "semiBold",
          fontSize: 4,
          display: "block",
          s: {
            my: 1
          }
        }, issueTitle, /*#__PURE__*/React.createElement(Badge, {
          color: "grey",
          display: "inline-block",
          s: {
            ml: 2
          }
        }, issueStatusName)));
      }
    case TopicFootprintSourceEnum.ASANA:
      {
        const {
          issueUrl,
          assigneeName,
          assigneeUrl,
          issueTitle,
          issueLabels,
          issueDueOn,
          issueStatusName
        } = footprint.data;
        return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Markdown, {
          weight: "semiBold",
          display: "block",
          color: "textSecondary",
          s: {
            mb: 1
          },
          text: `Changed [Task](${issueUrl})${assigneeName ? ` assigned to [${assigneeName}](${assigneeUrl})` : ""}${issueLabels?.length ? ` labelled with *${issueLabels.join("*, *")}` : ""}*${issueDueOn ? ` with due date on ${moment(issueDueOn, "YYYY-MM-DD").format("ddd, MMM D")}` : ""}`
        }), /*#__PURE__*/React.createElement(Text, {
          weight: "semiBold",
          fontSize: 4,
          display: "block",
          s: {
            my: 1
          }
        }, issueTitle, /*#__PURE__*/React.createElement(Badge, {
          color: "grey",
          display: "inline-block",
          s: {
            ml: 2
          }
        }, issueStatusName)));
      }
    case TopicFootprintSourceEnum.GOOGLE_MAIL:
      return /*#__PURE__*/React.createElement(TopicFootprintContentGmail, {
        footprint: footprint,
        hideMessage: true
      });
    default:
      return null;
  }
};
const TopicFootprintGroup = _ref4 => {
  let {
    topicId,
    group,
    selection,
    setSelection,
    ...rest
  } = _ref4;
  const isAllSelected = selection.includes("*");
  const isIncluded = group.footprints.every(footprint => selection.includes(footprint.id));
  const isSelected = !isAllSelected && isIncluded || isAllSelected && !isIncluded;
  const theme = useTheme();
  const {
    t
  } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [summary, setSummary] = useState();
  const [openNotes, setOpenNotes] = useState([]);
  const [isHovered, setIsHovered] = useState(false);
  const {
    userHas
  } = useAbacExt();
  const handleSummaryClick = () => {
    setLoading(true);
    setError(undefined);
    api.aiGenerateFootprintSummary(group.footprints.map(_ref5 => {
      let {
        id
      } = _ref5;
      return id;
    })).then(data => setSummary(data)).catch(e => setError(handleError(e))).finally(() => setLoading(false));
  };
  const handleSummaryCloseClick = () => {
    setSummary(undefined);
  };
  const handleSelectGroup = () => {
    setSelection(group.footprints.map(_ref6 => {
      let {
        id
      } = _ref6;
      return id;
    }));
  };
  const handleOpenNotes = id => {
    if (openNotes.includes(id)) {
      setOpenNotes(openNotes.filter(item => item !== id));
    } else {
      setOpenNotes([...openNotes, id]);
    }
  };
  return /*#__PURE__*/React.createElement(Grid, _extends({
    className: `topic__footprint-group${isSelected ? " topic__footprint-group--selected" : ""}${isHovered ? " topic__footprint--hovered" : ""}`,
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    cols: 4,
    gap: 6,
    template: "200px 6px 2.5rem 1fr",
    fullWidth: true
  }, rest), /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    template: userHas(AuthoritiesEnum.TOPICS_WRITE) ? "40px 1fr" : "1fr"
  }, userHas(AuthoritiesEnum.TOPICS_WRITE) && /*#__PURE__*/React.createElement(InputCheckbox, {
    value: isSelected,
    onChange: handleSelectGroup,
    className: "topic__footprint-checkbox",
    s: {
      ml: 4,
      mt: 1
    },
    color: isSelected ? "primary" : "grey"
  }), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    align: "right",
    s: {
      mt: 3
    }
  }, moment(group.date).local().startOf("seconds").fromNow(true), " ", t("Activities.ago"), group.isRetro && ` • ${t("TopicFootprint.retro")}`, /*#__PURE__*/React.createElement(Text, {
    className: "topic__footprint-absolute-date",
    color: "textSecondary",
    display: "block"
  }, moment(group.date).format("ddd, MMM D hh:mma")))), /*#__PURE__*/React.createElement(Timeline, {
    className: "topic__footprint-timeline",
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-float": true,
    "data-tooltip-content": lists.common.apps.names[group.source]
  }, /*#__PURE__*/React.createElement(SourcePicture, {
    border: "squared",
    src: lists.common.apps.logos(theme)[group.source],
    width: "1.5rem",
    height: "1.5rem",
    backgroundSize: "contain"
  })), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(AvatarGroup, null, group.members.slice(0, 3).map(member => /*#__PURE__*/React.createElement(Avatar, {
    key: member.id,
    size: 11,
    user: member
  }))), group.members.length > 3 && /*#__PURE__*/React.createElement(Text, {
    fontSize: 2.5,
    color: "textSecondary",
    weight: "semiBold",
    display: "block",
    align: "center"
  }, t("TopicFootprintGroup.plusMore", {
    count: group.members.slice(1, 3).length
  }))), /*#__PURE__*/React.createElement(Grid.Cell, {
    s: {
      mb: 6
    }
  }, /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold"
  }, "Group of ", group.members.length, " people"), groupContent(group), /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    s: {
      mr: 2
    },
    template: "1fr auto"
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, groupFootprintContent(group.footprints[0], false), group.footprints[0].labels.map(label => /*#__PURE__*/React.createElement(StyledBadge, {
    key: label.id,
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": label.description,
    color: label.color,
    fontSize: 2.8,
    s: {
      px: 2.5,
      py: 0.5,
      mr: 2,
      mb: 2
    },
    display: "inline-block",
    rounded: true
  }, label.title))), userHas(AuthoritiesEnum.TOPICS_WRITE) && /*#__PURE__*/React.createElement(TopicFootprintActions, {
    topicId: topicId,
    footprint: group.footprints[0],
    openNotes: openNotes.includes(group.footprints[0].id),
    onOpenNotes: handleOpenNotes
  }), openNotes.includes(group.footprints[0].id) && /*#__PURE__*/React.createElement(TopicFootprintNotes, {
    topicId: topicId,
    footprint: group.footprints[0]
  })), expanded && group.footprints.slice(1).map(footprint => /*#__PURE__*/React.createElement(Grid, {
    key: footprint.id,
    s: {
      mt: 5,
      mr: 2
    },
    cols: 2,
    template: "1fr auto"
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, groupFootprintContent(footprint, false), footprint.labels.map(label => /*#__PURE__*/React.createElement(StyledBadge, {
    key: label.id,
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": label.description,
    color: label.color,
    fontSize: 2.8,
    s: {
      px: 2.5,
      py: 0.5,
      mr: 2,
      mb: 2
    },
    display: "inline-block",
    rounded: true
  }, label.title))), userHas(AuthoritiesEnum.TOPICS_WRITE) && /*#__PURE__*/React.createElement(TopicFootprintActions, {
    topicId: topicId,
    footprint: footprint,
    openNotes: openNotes.includes(footprint.id),
    onOpenNotes: handleOpenNotes
  }), openNotes.includes(footprint.id) && /*#__PURE__*/React.createElement(TopicFootprintNotes, {
    topicId: topicId,
    footprint: footprint
  }))), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), (summary || loading) && /*#__PURE__*/React.createElement(Card, {
    s: {
      mt: 3,
      mb: 3,
      mr: 5
    },
    border: true
  }, loading ? /*#__PURE__*/React.createElement(Spinner, {
    s: {
      mt: 3,
      mb: 3
    },
    color: theme.colors.text.secondary
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold",
    fontSize: 4,
    display: "block",
    s: {
      mb: 2
    }
  }, t("TopicFootprintGroup.summary")), /*#__PURE__*/React.createElement(Markdown, {
    fontSize: 4,
    lineHeight: 6,
    text: summary,
    display: "block"
  }), /*#__PURE__*/React.createElement(CloseButton, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("common.actions.close"),
    onClick: handleSummaryCloseClick,
    color: "transparent",
    rounded: true,
    s: {
      p: 2,
      mx: 0.5
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    scale: 5,
    src: "cross",
    color: theme.colors.text.primary,
    display: "block"
  })))), /*#__PURE__*/React.createElement(Link, {
    color: theme.colors.text.secondary,
    onClick: () => setExpanded(prev => !prev),
    underline: true
  }, expanded ? "Collapse group" : t("TopicFootprintGroup.showMore", {
    count: group.footprints.length - 1
  })), [TopicFootprintSourceEnum.SLACK].includes(group.source) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    color: theme.colors.text.secondary,
    s: {
      mx: 1
    }
  }, t("TopicFootprintGroup.or")), /*#__PURE__*/React.createElement(Link, {
    onClick: handleSummaryClick,
    color: theme.colors.text.secondary,
    underline: true
  }, t("TopicFootprintGroup.summarize")))));
};
export default TopicFootprintGroup;